import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, X } from "lucide-react";
import { UomCombobox } from "@/components/uom-combobox";

type Config = Record<string, unknown>;

interface Props {
  transformType: string;
  config: Config | null | undefined;
  sourceColumns?: string[];
  onChange: (config: Config) => void;
}

const DATE_FORMATS = [
  "DD/MM/YYYY",
  "MM/DD/YYYY",
  "DD-MM-YYYY",
  "MM-DD-YYYY",
  "YYYY-MM-DD",
];

const TRANSFORMS_WITH_CONFIG = new Set([
  "date_reformat",
  "value_map",
  "strip_chars",
  "pad_left",
  "svc_to_total",
  "add_columns",
  "enum_tax_type",
  "enum_classification",
  "enum_measurement",
  "enum_state",
  "enum_payment_mode",
  "enum_registration_type",
]);

export const transformNeedsConfig = (transformType: string): boolean =>
  TRANSFORMS_WITH_CONFIG.has(transformType);

/**
 * Inline editor for a column mapping's transform configuration
 */
export const TransformConfigEditor = ({
  transformType,
  config,
  sourceColumns = [],
  onChange,
}: Props) => {
  const cfg = config ?? {};

  const set = (patch: Config) => onChange({ ...cfg, ...patch });

  // ── date_reformat ──
  if (transformType === "date_reformat") {
    const current =
      (cfg.inputFormat as string) || (cfg.fromFormat as string) || "DD/MM/YYYY";
    return (
      <div className="flex items-center gap-2 text-xs">
        <span className="text-muted-foreground">Input format</span>
        <Select
          value={current}
          onValueChange={(v) => onChange({ inputFormat: v })}
        >
          <SelectTrigger className="h-7 w-40 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DATE_FORMATS.map((f) => (
              <SelectItem key={f} value={f}>
                {f}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  // ── pad_left ──
  if (transformType === "pad_left") {
    return (
      <div className="flex items-center gap-2 text-xs">
        <span className="text-muted-foreground">Length</span>
        <Input
          className="h-7 w-16 text-xs"
          type="number"
          value={(cfg.length as number) ?? 3}
          onChange={(e) => set({ length: Number(e.target.value) })}
        />
        <span className="text-muted-foreground">Pad char</span>
        <Input
          className="h-7 w-14 text-xs"
          value={(cfg.char as string) ?? "0"}
          onChange={(e) => set({ char: e.target.value })}
        />
      </div>
    );
  }

  // ── strip_chars ──
  if (transformType === "strip_chars") {
    const chars = Array.isArray(cfg.chars)
      ? (cfg.chars as string[]).join("")
      : "";
    return (
      <div className="flex items-center gap-2 text-xs">
        <span className="text-muted-foreground">Strip characters</span>
        <Input
          className="h-7 w-40 text-xs"
          placeholder="e.g. -# "
          value={chars}
          onChange={(e) => onChange({ chars: e.target.value.split("") })}
        />
      </div>
    );
  }

  // ── svc_to_total ──
  if (transformType === "svc_to_total") {
    return (
      <div className="flex items-center gap-2 text-xs">
        <span className="text-muted-foreground">Service charge column</span>
        {sourceColumns.length > 0 ? (
          <Select
            value={(cfg.svcColumn as string) || undefined}
            onValueChange={(v) => onChange({ svcColumn: v })}
          >
            <SelectTrigger className="h-7 w-48 text-xs">
              <SelectValue placeholder="Select column" />
            </SelectTrigger>
            <SelectContent>
              {sourceColumns.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            className="h-7 w-48 text-xs"
            value={(cfg.svcColumn as string) ?? ""}
            onChange={(e) => onChange({ svcColumn: e.target.value })}
          />
        )}
      </div>
    );
  }

  // ── add_columns ──
  if (transformType === "add_columns") {
    const operands = (cfg.operands as string[]) ?? [];
    const setOperand = (i: number, val: string) => {
      const next = [...operands];
      next[i] = val;
      onChange({ operands: next });
    };
    return (
      <div className="flex flex-col gap-1 text-xs">
        <span className="text-muted-foreground">Columns to sum</span>
        {operands.map((op, i) => (
          <div key={i} className="flex items-center gap-1">
            {sourceColumns.length > 0 ? (
              <Select
                value={op || undefined}
                onValueChange={(v) => setOperand(i, v)}
              >
                <SelectTrigger className="h-7 w-48 text-xs">
                  <SelectValue placeholder="Select column" />
                </SelectTrigger>
                <SelectContent>
                  {sourceColumns.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                className="h-7 w-48 text-xs"
                value={op}
                onChange={(e) => setOperand(i, e.target.value)}
              />
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() =>
                onChange({
                  operands: operands.filter((_, idx) => idx !== i),
                })
              }
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 w-fit text-xs"
          onClick={() => onChange({ operands: [...operands, ""] })}
        >
          <Plus className="h-3 w-3 mr-1" /> Add column
        </Button>
      </div>
    );
  }

  // ── enum_measurement ──
  if (transformType === "enum_measurement") {
    const aliases = (cfg.custom_aliases as Record<string, string>) ?? {};
    const aliasEntries = Object.entries(aliases);

    const setAlias = (oldKey: string, key: string, val: string) => {
      const next: Record<string, string> = {};
      for (const [k, v] of aliasEntries) {
        if (k === oldKey) next[key] = val;
        else next[k] = v;
      }
      onChange({ ...cfg, custom_aliases: next });
    };

    const fallback = (cfg.fallback as string) ?? "";

    return (
      <div className="flex flex-col gap-2 text-xs">
        <p className="text-muted-foreground">
          Common units are already understood — <code>kg</code>, <code>KG</code>
          , <code>kgs</code> and <code>kilogram</code> all become{" "}
          <span className="font-mono">KGM</span>. Add an alias only for wording
          this vendor uses that is not standard.
        </p>

        <div className="flex items-center gap-2">
          <span className="text-muted-foreground w-24 shrink-0">
            Fallback unit
          </span>
          <UomCombobox
            value={fallback}
            onChange={(code) => set({ fallback: code ?? "" })}
            className="h-7 w-64 text-xs"
            placeholder="None — leave the unit empty"
          />
        </div>
        <p className="text-muted-foreground pl-26">
          Used when a row&rsquo;s unit is blank or cannot be matched. Leave it
          unset to send no unit for those rows, which LHDN accepts.
        </p>

        <span className="text-muted-foreground mt-1">
          Custom aliases (this vendor&rsquo;s wording &rarr; LHDN unit)
        </span>
        {aliasEntries.map(([k, v], i) => (
          <div key={i} className="flex items-center gap-1">
            <Input
              className="h-7 w-32 text-xs"
              placeholder="e.g. NOS"
              value={k}
              onChange={(e) => setAlias(k, e.target.value, v)}
            />
            <span>&rarr;</span>
            <UomCombobox
              value={v}
              onChange={(code) => setAlias(k, k, code ?? "")}
              className="h-7 w-56 text-xs"
              placeholder="Select unit"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => {
                const next = { ...aliases };
                delete next[k];
                onChange({ ...cfg, custom_aliases: next });
              }}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 w-fit text-xs"
          disabled={aliasEntries.some(([k]) => k === "")}
          onClick={() =>
            onChange({ ...cfg, custom_aliases: { ...aliases, "": "" } })
          }
        >
          <Plus className="h-3 w-3 mr-1" /> Add alias
        </Button>
      </div>
    );
  }

  // ── enum_* (fallback + custom aliases) ──
  if (transformType.startsWith("enum_")) {
    const aliases = (cfg.custom_aliases as Record<string, string>) ?? {};
    const aliasEntries = Object.entries(aliases);
    const setAlias = (oldKey: string, key: string, val: string) => {
      const next: Record<string, string> = {};
      for (const [k, v] of aliasEntries) {
        if (k === oldKey) next[key] = val;
        else next[k] = v;
      }
      onChange({ ...cfg, custom_aliases: next });
    };
    return (
      <div className="flex flex-col gap-1 text-xs">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Fallback value</span>
          <Input
            className="h-7 w-24 text-xs"
            value={(cfg.fallback as string) ?? ""}
            onChange={(e) => set({ fallback: e.target.value })}
          />
        </div>
        <span className="text-muted-foreground">Custom aliases</span>
        {aliasEntries.map(([k, v], i) => (
          <div key={i} className="flex items-center gap-1">
            <Input
              className="h-7 w-32 text-xs"
              placeholder="source value"
              value={k}
              onChange={(e) => setAlias(k, e.target.value, v)}
            />
            <span>&rarr;</span>
            <Input
              className="h-7 w-24 text-xs"
              placeholder="code"
              value={v}
              onChange={(e) => setAlias(k, k, e.target.value)}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => {
                const next = { ...aliases };
                delete next[k];
                onChange({ ...cfg, custom_aliases: next });
              }}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 w-fit text-xs"
          onClick={() =>
            onChange({
              ...cfg,
              custom_aliases: { ...aliases, "": "" },
            })
          }
        >
          <Plus className="h-3 w-3 mr-1" /> Add alias
        </Button>
      </div>
    );
  }

  // ── value_map ──
  if (transformType === "value_map") {
    const map = (cfg.map as Record<string, string>) ?? {};
    const entries = Object.entries(map);
    const setEntry = (oldKey: string, key: string, val: string) => {
      const next: Record<string, string> = {};
      for (const [k, v] of entries) {
        if (k === oldKey) next[key] = val;
        else next[k] = v;
      }
      onChange({ map: next });
    };
    return (
      <div className="flex flex-col gap-1 text-xs">
        <span className="text-muted-foreground">Value map</span>
        {entries.map(([k, v], i) => (
          <div key={i} className="flex items-center gap-1">
            <Input
              className="h-7 w-32 text-xs"
              placeholder="from"
              value={k}
              onChange={(e) => setEntry(k, e.target.value, v)}
            />
            <span>&rarr;</span>
            <Input
              className="h-7 w-32 text-xs"
              placeholder="to"
              value={v}
              onChange={(e) => setEntry(k, k, e.target.value)}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => {
                const next = { ...map };
                delete next[k];
                onChange({ map: next });
              }}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 w-fit text-xs"
          onClick={() => onChange({ map: { ...map, "": "" } })}
        >
          <Plus className="h-3 w-3 mr-1" /> Add mapping
        </Button>
      </div>
    );
  }

  return null;
};

export default TransformConfigEditor;
